import { ApiService } from "@core/api/api-service";
import { Page } from "@core/pagination/page";
import {
	LoadNotificationOutput,
	Notification,
	NotificationId,
	NotificationObject,
	NotificationType,
} from "@domain/notification/notification";
import { HctInvitationAcceptedNotification } from "@domain/notification/notifications/hct-invitation-accepted-notification";
import { HctInvitationSentNotification } from "@domain/notification/notifications/hct-invitation-sent-notification";
import { PatientHasDeletedHisAccountNotification } from "@domain/notification/notifications/patient-has-deleted-his-account-notification";
import { PatientProgramEndedNotification } from "@domain/notification/notifications/patient-program-ended-notification";
import { PatientProgramEndingSoonNotification } from "@domain/notification/notifications/patient-program-ending-soon-notification";
import { PatientProgramStartedNotification } from "@domain/notification/notifications/patient-program-started-notification";
import { PatientRegisteredNotification } from "@domain/notification/notifications/patient-registered-notification";
import { PtAccountValidatedNotification } from "@domain/notification/notifications/pt-account-validated-notification";
import { PtAccountValidationPendingNotification } from "@domain/notification/notifications/pt-account-validation-pending-notification";
import { dateFromDateDto } from "@utils/date-utils";
import { PtLeftHctNotification } from "@domain/notification/notifications/patient-left-hct-notification";

type NotificationObjectDto = Omit<NotificationObject, "creationDate"> & {
	creationDate: string;
};

type NotificationsOutput = {
	notifications: Page<NotificationObjectDto>;
	unreadNotificationsCount: number;
};

export class NotificationService {
	constructor(private readonly apiService: ApiService) {}

	async loadNotifications(size: number, offset = 0): Promise<LoadNotificationOutput> {
		const result = await this.apiService.get<NotificationsOutput>(
			`/pts/me/notifications?size=${size}&offset=${offset}`,
		);
		return {
			notifications: result.data.notifications.content.map(notification =>
				NotificationService.notificationFromDto(notification),
			),
			reachedEnd: result.data.notifications.last,
			unreadNotificationsCount: result.data.unreadNotificationsCount,
		};
	}

	async markAllAsRead(): Promise<void> {
		await this.apiService.post<void>("/pts/me/notifications/mark-as-read");
	}

	async markAsRead(notificationId: NotificationId): Promise<void> {
		await this.apiService.post<void>(`/pts/me/notifications/${notificationId}/mark-as-read`);
	}

	async markAsUnread(notificationId: NotificationId): Promise<void> {
		await this.apiService.post<void>(`/pts/me/notifications/${notificationId}/mark-as-unread`);
	}

	async getUnsentNotifications(): Promise<Notification[]> {
		const result = await this.apiService.get<NotificationObjectDto[]>("/pts/me/notifications/unsent");
		return result.data.map(notification => NotificationService.notificationFromDto(notification));
	}

	static notificationFromDto(dto: NotificationObjectDto): Notification {
		const notification: NotificationObject = { ...dto, creationDate: dateFromDateDto(dto.creationDate) };

		switch (notification.type) {
			case NotificationType.PATIENT_PROGRAM_STARTED:
				return new PatientProgramStartedNotification(notification);
			case NotificationType.PATIENT_PROGRAM_ENDING_SOON:
				return new PatientProgramEndingSoonNotification(notification);
			case NotificationType.PATIENT_PROGRAM_ENDED:
				return new PatientProgramEndedNotification(notification);
			case NotificationType.PATIENT_REGISTERED:
				return new PatientRegisteredNotification(notification);
			case NotificationType.HCT_INVITATION_SENT:
				return new HctInvitationSentNotification(notification);
			case NotificationType.HCT_INVITATION_ACCEPTED:
				return new HctInvitationAcceptedNotification(notification);
			case NotificationType.PT_ACCOUNT_VALIDATED:
				return new PtAccountValidatedNotification(notification);
			case NotificationType.PT_ACCOUNT_VALIDATION_PENDING:
				return new PtAccountValidationPendingNotification(notification);
			case NotificationType.PATIENT_HAS_DELETED_HIS_ACCOUNT:
				return new PatientHasDeletedHisAccountNotification(notification);
			case NotificationType.PT_LEFT_HCT:
				return new PtLeftHctNotification(notification);
		}
	}
}
